import React, { useEffect, useState } from "react";
import { ArrowLeft, Upload } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import { getAllRewardProgramsList } from "../../services/Admin/commonService";
import {
  createTransferPartners,
  detailsTransferPartners,
  updateTransferPartners,
} from "../../services/Admin/transferPartnersService";
import Select from "react-select";

const AddTransferPartners = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    from_program_id: "",
    to_program_id: "",
    transfer_ratio: "",
    bonus_percentage: "",
    starts_at: "",
    ends_at: 0,
    is_active: 1,
    uuid: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    from_program_id: "",
    to_program_id: "",
    transfer_ratio: "",
    bonus_percentage: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [rewardProgramsList, setRewardProgramsList] = useState([]);
  const [transferDetails, setTransferDetails] = useState("");

  const validateField = (name, value) => {
    switch (name) {
      case "from_program_id":
        if (!value) {
          return "From program is required";
        }

        if (
          formData.to_program_id &&
          String(value) === String(formData.to_program_id)
        ) {
          return "From Program and To Program cannot be same";
        }
        return "";

      case "to_program_id":
        if (!value) {
          return "To program is required";
        }

        if (
          formData.from_program_id &&
          String(value) === String(formData.from_program_id)
        ) {
          return "From Program and To Program cannot be same";
        }
        return "";

      case "transfer_ratio":
        if (!value) {
          return "Transfer ratio is required";
        }
        return "";

      default:
        return "";
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  useEffect(() => {
    getAllRewardProgramsList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);

        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        setRewardProgramsList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (uuid) {
      let data = {
        uuid: uuid,
      };
      detailsTransferPartners(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);
          setTransferDetails(decrypted.data);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [uuid]);

  useEffect(() => {
    if (transferDetails) {
      setFormData({
        name: transferDetails?.name,
        from_program_id: transferDetails?.from_program_id,
        to_program_id: transferDetails?.to_program_id,
        transfer_ratio: transferDetails?.transfer_ratio,
        bonus_percentage: transferDetails?.bonus_percentage,
        is_active: transferDetails?.is_active,
        uuid: transferDetails?.uuid,
      });
    }
  }, [transferDetails]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      from_program_id: validateField(
        "from_program_id",
        formData.from_program_id,
      ),
      name: validateField("name", formData.name),
      to_program_id: validateField("to_program_id", formData.to_program_id),
      transfer_ratio: validateField("transfer_ratio", formData.transfer_ratio),
    };

    setErrors(newErrors);
    Utils.focusError();
    const hasErrors = Object.values(newErrors).some((err) => err);
    if (hasErrors) return;

    try {
      setBtnLoading(true);
      const payload = {
        name: formData.name,
        from_program_id: formData.from_program_id,
        to_program_id: formData.to_program_id,
        transfer_ratio: formData.transfer_ratio,
        bonus_percentage: formData.bonus_percentage,

        is_active: formData.is_active,
        ...(formData?.uuid && {
          uuid: formData.uuid,
        }),
      };

      const apiCall = formData?.uuid
        ? updateTransferPartners
        : createTransferPartners;

      const response = await apiCall(payload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      setFormData({
        from_program_id: "",
        to_program_id: "",
        transfer_ratio: "",
        bonus_percentage: "",
        is_active: 1,
        uuid: "",
      });
      setErrors({
        from_program_id: "",
        to_program_id: "",
        transfer_ratio: "",
      });
      navigate("/transfer-partners");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div className="pb-24 md:pb-0 transition-colors duration-300">
      <div className="w-full bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm p-5 md:p-8">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/transfer-partners")}
            className="p-2 -ml-2 text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h2 className="text-xl md:text-2xl font-black text-slate-800 dark:text-white tracking-tight">
            {uuid ? "Update Transfer Partners" : "Add Transfer Partners"}
          </h2>
        </div>

        <div className="space-y-6 md:space-y-8">
          <div>
            <h3 className="text-[10px] md:text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-5 border-b border-slate-50 dark:border-slate-700/50 pb-2">
              Transfer Partners Information
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6">
              <div className="space-y-5">
                <div className="space-y-1.5">
                  <label htmlFor="from_program_id" className="input-label">
                    From Program<span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={rewardProgramsList}
                    value={getSelectedOption(
                      rewardProgramsList,
                      formData.from_program_id,
                    )}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        from_program_id: String(selected?.value ?? ""),
                      }));
                    }}
                    placeholder="Select from program"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {errors.from_program_id && (
                    <span className="text-danger">
                      {errors.from_program_id}
                    </span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="from_program_id" className="input-label">
                    To Program<span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={rewardProgramsList}
                    value={getSelectedOption(
                      rewardProgramsList,
                      formData.to_program_id,
                    )}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        to_program_id: String(selected?.value ?? ""),
                      }));
                    }}
                    placeholder="Select To program"
                    isClearable
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    }}
                  />
                  {errors.to_program_id && (
                    <span className="text-danger">{errors.to_program_id}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="transfer_ratio" className="input-label">
                    Transfer ratio<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="transfer_ratio"
                    className="input-field"
                    placeholder="Enter transfer ratio"
                    value={formData?.transfer_ratio || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                  {errors.transfer_ratio && (
                    <span className="text-danger">{errors.transfer_ratio}</span>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="bonus_percentage" className="input-label">
                    Bonus Percentage
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    name="bonus_percentage"
                    className="input-field"
                    placeholder="Enter bonus percentage"
                    value={formData?.bonus_percentage || ""}
                    onChange={handleChange}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className="lg:col-span-2 space-y-3">
                <label className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-6">
                  {["Active", "Inactive"].map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <div className="relative flex items-center justify-center">
                        <input
                          type="radio"
                          name="status"
                          className="peer shrink-0 appearance-none w-4 h-4 border border-slate-300 dark:border-slate-600 rounded-full bg-white dark:bg-slate-900 checked:border-orange-500 dark:checked:border-orange-600 transition-all"
                          defaultChecked={status === "Active"}
                        />
                        <div className="absolute w-2 h-2 rounded-full bg-orange-500 opacity-0 peer-checked:opacity-100 transition-opacity" />
                      </div>
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200 transition-colors">
                        {status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-4 pt-6 mt-6 border-t border-slate-50 dark:border-slate-700/50">
            <button
              type="button"
              onClick={() => navigate("/transfer-partners")}
              className="flex-1 md:flex-none px-6 py-3 rounded-xl font-black text-xs text-slate-400 dark:text-slate-500"
            >
              Cancel
            </button>
            <button
              type="button"
              form="reward-form"
              className="flex-[2] md:flex-none px-8 py-3 bg-[#D4AF37] text-white rounded-xl font-black text-xs shadow-lg shadow-orange-200/50"
              onClick={handleSubmit}
              disabled={btnLoading}
            >
              {btnLoading && (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              )}
              {btnLoading
                ? "submitting..."
                : formData.uuid
                  ? "Update"
                  : "Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTransferPartners;
